import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Progress } from '../../components/ui/progress';
import { 
  Building2, CreditCard, FileSignature, CheckCircle, ArrowRight, ArrowLeft,
  User, MapPin, Phone, Mail, Briefcase, Loader2, AlertCircle, Shield
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';
import api from '../../lib/axios';

const AffiliateOnboardingPage = () => {
  const { user, refreshUser } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  
  // Form data
  const [formData, setFormData] = useState({
    // Personal info
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    
    // Company info
    company_name: '',
    siret: '',
    vat_number: '',
    legal_status: '',
    company_address: '',
    company_city: '',
    company_postal_code: '',
    company_country: 'France',
    
    // Bank info
    iban: '',
    bic: '',
    bank_name: '',
    account_holder: '',
    
    // Contract
    accept_terms: false,
    accept_gdpr: false
  });

  const [errors, setErrors] = useState({});

  // Load existing data
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoadingData(true);
        const res = await api.get('/affiliates/me/profile');
        if (res.data) {
          setFormData(prev => ({
            ...prev,
            first_name: user?.first_name || '',
            last_name: user?.last_name || '',
            email: user?.email || '',
            phone: user?.phone || '',
            ...res.data
          }));
        }
      } catch (error) {
        // No existing data, use user info
        setFormData(prev => ({
          ...prev,
          first_name: user?.first_name || '',
          last_name: user?.last_name || '',
          email: user?.email || '',
          phone: user?.phone || '',
          account_holder: `${user?.first_name || ''} ${user?.last_name || ''}`.trim()
        }));
      } finally {
        setLoadingData(false);
      }
    };
    loadData();
  }, [user]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validateStep = (step) => {
    const newErrors = {};
    
    if (step === 1) {
      if (!formData.first_name) newErrors.first_name = 'Prénom requis';
      if (!formData.last_name) newErrors.last_name = 'Nom requis';
      if (!formData.email) newErrors.email = 'Email requis';
      if (!formData.phone) newErrors.phone = 'Téléphone requis';
    }
    
    if (step === 2) {
      if (!formData.company_name) newErrors.company_name = 'Nom de la société requis';
      if (!formData.siret) newErrors.siret = 'SIRET requis';
      else if (formData.siret.replace(/\s/g, '').length !== 14) newErrors.siret = 'SIRET invalide (14 chiffres)';
      if (!formData.company_address) newErrors.company_address = 'Adresse requise';
      if (!formData.company_city) newErrors.company_city = 'Ville requise';
      if (!formData.company_postal_code) newErrors.company_postal_code = 'Code postal requis';
    }
    
    if (step === 3) {
      // Seuls IBAN et BIC sont obligatoires comme demandé par l'utilisateur
      if (!formData.iban) newErrors.iban = 'IBAN requis';
      else if (formData.iban.replace(/\s/g, '').length < 15) newErrors.iban = 'IBAN invalide';
      if (!formData.bic) newErrors.bic = 'BIC requis';
      // bank_name et account_holder sont optionnels
    }
    
    if (step === 4) {
      if (!formData.accept_terms) newErrors.accept_terms = 'Vous devez accepter les conditions';
      if (!formData.accept_gdpr) newErrors.accept_gdpr = 'Vous devez accepter la politique RGPD';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 4));
    }
  };

  const handlePrev = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    if (!validateStep(4)) return;
    
    setLoading(true);
    try {
      await api.put('/affiliates/me/profile', {
        company_name: formData.company_name,
        siret: formData.siret.replace(/\s/g, ''),
        vat_number: formData.vat_number,
        legal_status: formData.legal_status,
        company_address: formData.company_address,
        company_city: formData.company_city,
        company_postal_code: formData.company_postal_code,
        company_country: formData.company_country,
        iban: formData.iban.replace(/\s/g, ''),
        bic: formData.bic,
        bank_name: formData.bank_name,
        account_holder: formData.account_holder,
        onboarding_completed: true,
        onboarding_completed_at: new Date().toISOString()
      });
      
      // Update user info if changed
      await api.put('/users/me', {
        phone: formData.phone
      });
      
      toast.success('Profil complété avec succès !');
      
      if (refreshUser) {
        await refreshUser();
      }
      
      navigate('/affiliate/dashboard');
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    { num: 1, title: 'Informations personnelles', icon: User },
    { num: 2, title: 'Société', icon: Building2 },
    { num: 3, title: 'Coordonnées bancaires', icon: CreditCard },
    { num: 4, title: 'Validation', icon: FileSignature }
  ];

  const progress = (currentStep / 4) * 100;

  if (loadingData) {
    return (
      <div className="min-h-screen bg-[#f8fafc] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />
      <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />
      
      <main className={cn("transition-all duration-300 px-6 md:px-8 pb-6 md:pb-8", collapsed ? "md:ml-20" : "md:ml-64")}
        style={{ paddingTop: "130px" }}
      >
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="mb-8 text-center">
            <h1 className="text-2xl font-bold" style={{ color: theme.colors?.primary }}>
              Compléter mon profil Apporteur
            </h1>
            <p className="text-slate-500 mt-1">
              Renseignez vos informations pour recevoir vos commissions
            </p>
          </div>

          {/* Progress */}
          <div className="mb-8">
            <Progress value={progress} className="h-2 mb-4" />
            <div className="flex justify-between">
              {steps.map((step) => {
                const StepIcon = step.icon;
                const isActive = currentStep === step.num;
                const isCompleted = currentStep > step.num;
                return (
                  <div key={step.num} className="flex flex-col items-center">
                    <div className={cn(
                      "w-10 h-10 rounded-full flex items-center justify-center mb-2 transition-colors",
                      isCompleted ? "bg-emerald-500 text-white" :
                      isActive ? "bg-blue-500 text-white" : "bg-slate-200 text-slate-500"
                    )}>
                      {isCompleted ? <CheckCircle className="w-5 h-5" /> : <StepIcon className="w-5 h-5" />}
                    </div>
                    <span className={cn(
                      "text-xs text-center hidden sm:block",
                      isActive ? "text-blue-600 font-medium" : "text-slate-500"
                    )}>
                      {step.title}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Step Content */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                {React.createElement(steps[currentStep - 1].icon, { 
                  className: "w-5 h-5", 
                  style: { color: theme.colors?.accent } 
                })}
                {steps[currentStep - 1].title}
              </CardTitle>
              <CardDescription>
                {currentStep === 1 && "Vérifiez vos informations de contact"}
                {currentStep === 2 && "Informations de votre structure juridique"}
                {currentStep === 3 && "Pour recevoir vos commissions"}
                {currentStep === 4 && "Acceptez les conditions pour finaliser"}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Step 1: Personal Info */}
              {currentStep === 1 && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="first_name">Prénom *</Label>
                      <Input
                        id="first_name"
                        value={formData.first_name}
                        onChange={(e) => handleChange('first_name', e.target.value)}
                        className={errors.first_name ? 'border-red-500' : ''}
                      />
                      {errors.first_name && <p className="text-xs text-red-500">{errors.first_name}</p>}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="last_name">Nom *</Label>
                      <Input
                        id="last_name"
                        value={formData.last_name}
                        onChange={(e) => handleChange('last_name', e.target.value)}
                        className={errors.last_name ? 'border-red-500' : ''}
                      />
                      {errors.last_name && <p className="text-xs text-red-500">{errors.last_name}</p>}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleChange('email', e.target.value)}
                      className={errors.email ? 'border-red-500' : ''}
                      disabled
                    />
                    {errors.email && <p className="text-xs text-red-500">{errors.email}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Téléphone *</Label>
                    <Input
                      id="phone"
                      value={formData.phone}
                      onChange={(e) => handleChange('phone', e.target.value)}
                      placeholder="06 12 34 56 78"
                      className={errors.phone ? 'border-red-500' : ''}
                    />
                    {errors.phone && <p className="text-xs text-red-500">{errors.phone}</p>}
                  </div>
                </>
              )}

              {/* Step 2: Company Info */}
              {currentStep === 2 && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="company_name">Nom de la société *</Label>
                    <Input
                      id="company_name"
                      value={formData.company_name}
                      onChange={(e) => handleChange('company_name', e.target.value)}
                      placeholder="Ma Société SARL"
                      className={errors.company_name ? 'border-red-500' : ''}
                    />
                    {errors.company_name && <p className="text-xs text-red-500">{errors.company_name}</p>}
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="siret">SIRET *</Label>
                      <Input
                        id="siret"
                        value={formData.siret}
                        onChange={(e) => handleChange('siret', e.target.value)}
                        placeholder="123 456 789 00012"
                        className={errors.siret ? 'border-red-500' : ''}
                      />
                      {errors.siret && <p className="text-xs text-red-500">{errors.siret}</p>}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="vat_number">N° TVA (optionnel)</Label>
                      <Input
                        id="vat_number"
                        value={formData.vat_number}
                        onChange={(e) => handleChange('vat_number', e.target.value)}
                        placeholder="FR12345678901"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="legal_status">Forme juridique</Label>
                    <Input
                      id="legal_status"
                      value={formData.legal_status}
                      onChange={(e) => handleChange('legal_status', e.target.value)}
                      placeholder="SARL, SAS, Auto-entrepreneur..."
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="company_address">Adresse *</Label>
                    <Input
                      id="company_address"
                      value={formData.company_address}
                      onChange={(e) => handleChange('company_address', e.target.value)}
                      placeholder="123 rue de la Paix"
                      className={errors.company_address ? 'border-red-500' : ''}
                    />
                    {errors.company_address && <p className="text-xs text-red-500">{errors.company_address}</p>}
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="company_postal_code">Code postal *</Label>
                      <Input
                        id="company_postal_code"
                        value={formData.company_postal_code}
                        onChange={(e) => handleChange('company_postal_code', e.target.value)}
                        placeholder="75001"
                        className={errors.company_postal_code ? 'border-red-500' : ''}
                      />
                      {errors.company_postal_code && <p className="text-xs text-red-500">{errors.company_postal_code}</p>}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="company_city">Ville *</Label>
                      <Input
                        id="company_city"
                        value={formData.company_city}
                        onChange={(e) => handleChange('company_city', e.target.value)}
                        placeholder="Paris"
                        className={errors.company_city ? 'border-red-500' : ''}
                      />
                      {errors.company_city && <p className="text-xs text-red-500">{errors.company_city}</p>}
                    </div>
                  </div>
                </>
              )}

              {/* Step 3: Bank Info */}
              {currentStep === 3 && (
                <>
                  <div className="p-3 bg-blue-50 rounded-lg mb-4 flex items-start gap-2">
                    <Shield className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-blue-700">
                      Vos coordonnées bancaires sont sécurisées et ne seront utilisées que pour le versement de vos commissions.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="iban">IBAN *</Label>
                    <Input
                      id="iban"
                      value={formData.iban}
                      onChange={(e) => handleChange('iban', e.target.value.toUpperCase())}
                      placeholder="FR76 1234 5678 9012 3456 7890 123"
                      className={cn("font-mono", errors.iban ? 'border-red-500' : '')}
                    />
                    {errors.iban && <p className="text-xs text-red-500">{errors.iban}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="bic">BIC / SWIFT *</Label>
                    <Input
                      id="bic"
                      value={formData.bic}
                      onChange={(e) => handleChange('bic', e.target.value.toUpperCase())}
                      placeholder="AGRIFRPP"
                      className={cn("font-mono", errors.bic ? 'border-red-500' : '')}
                    />
                    {errors.bic && <p className="text-xs text-red-500">{errors.bic}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="account_holder">Titulaire du compte (optionnel)</Label>
                    <Input
                      id="account_holder"
                      value={formData.account_holder}
                      onChange={(e) => handleChange('account_holder', e.target.value)}
                      placeholder="Nom tel qu'il apparaît sur le compte"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="bank_name">Nom de la banque (optionnel)</Label>
                    <Input
                      id="bank_name"
                      value={formData.bank_name}
                      onChange={(e) => handleChange('bank_name', e.target.value)}
                      placeholder="Crédit Agricole, BNP Paribas..."
                    />
                  </div>
                </>
              )}

              {/* Step 4: Validation */}
              {currentStep === 4 && (
                <>
                  {/* Summary */}
                  <div className="space-y-4 mb-6">
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <h4 className="font-medium mb-2 flex items-center gap-2">
                        <User className="w-4 h-4" /> Informations personnelles
                      </h4>
                      <p className="text-sm text-slate-600">
                        {formData.first_name} {formData.last_name}<br />
                        {formData.email} • {formData.phone}
                      </p>
                    </div>
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <h4 className="font-medium mb-2 flex items-center gap-2">
                        <Building2 className="w-4 h-4" /> Société
                      </h4>
                      <p className="text-sm text-slate-600">
                        {formData.company_name}<br />
                        SIRET: {formData.siret}<br />
                        {formData.company_address}, {formData.company_postal_code} {formData.company_city}
                      </p>
                    </div>
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <h4 className="font-medium mb-2 flex items-center gap-2">
                        <CreditCard className="w-4 h-4" /> Coordonnées bancaires
                      </h4>
                      <p className="text-sm text-slate-600">
                        {formData.bank_name} • {formData.account_holder}<br />
                        <span className="font-mono">IBAN: {formData.iban.slice(0, 4)}...{formData.iban.slice(-4)}</span>
                      </p>
                    </div>
                  </div>

                  {/* Terms */}
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <Checkbox
                        id="accept_terms"
                        checked={formData.accept_terms}
                        onCheckedChange={(checked) => handleChange('accept_terms', checked)}
                      />
                      <label htmlFor="accept_terms" className="text-sm text-slate-600 cursor-pointer">
                        J&apos;accepte les <a href="/affiliate/contract" className="text-blue-600 underline" target="_blank">conditions générales du programme apporteur d&apos;affaires</a> et je certifie l&apos;exactitude des informations fournies. *
                      </label>
                    </div>
                    {errors.accept_terms && <p className="text-xs text-red-500 ml-7">{errors.accept_terms}</p>}
                    
                    <div className="flex items-start gap-3">
                      <Checkbox
                        id="accept_gdpr"
                        checked={formData.accept_gdpr}
                        onCheckedChange={(checked) => handleChange('accept_gdpr', checked)}
                      />
                      <label htmlFor="accept_gdpr" className="text-sm text-slate-600 cursor-pointer">
                        J&apos;accepte que mes données soient traitées conformément à la <span className="text-blue-600">politique de confidentialité</span> pour la gestion de mon compte apporteur. *
                      </label>
                    </div>
                    {errors.accept_gdpr && <p className="text-xs text-red-500 ml-7">{errors.accept_gdpr}</p>}
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-6">
            <Button
              variant="outline"
              onClick={handlePrev}
              disabled={currentStep === 1}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Précédent
            </Button>
            
            {currentStep < 4 ? (
              <Button
                onClick={handleNext}
                style={{ backgroundColor: theme.colors?.accent }}
              >
                Suivant
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={loading}
                style={{ backgroundColor: theme.colors?.accent }}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Enregistrement...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Valider mon profil
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default AffiliateOnboardingPage;
